import React, { useEffect, useState } from "react";
import { KeyRound, Copy, Check } from "lucide-react";
import { api } from "@/lib/api";
import { fmtTimeAgo } from "@/lib/labels";

/**
 * Machine credentials, and the mapping guide for using one.
 *
 * This is the whole of the "internal ticket database" connector: the client's
 * own system posts to Prioris with a key. Nothing of theirs is stored here, no
 * database credentials change hands, and no inbound access to their network is
 * needed — which is why it is the one integration a pilot gets.
 */
export default function SettingsApiKeys() {
  const [accounts, setAccounts] = useState(null);
  const [name, setName] = useState("");
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    api.get("/service-accounts")
      .then((r) => setAccounts(r.data.service_accounts || []))
      .catch(() => setAccounts([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/service-accounts", { name });
      setIssued(data);
      setName("");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not issue a key");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (a) => {
    if (!window.confirm(
      `Revoke "${a.name}"?\n\n` +
      "Anything using this key stops being able to push signals immediately. " +
      "Signals already sent are unaffected."
    )) return;
    try {
      await api.post(`/service-accounts/${a.id}/revoke`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not revoke that key");
    }
  };

  return (
    <div>
      <p className="text-[12.5px] mb-4" style={{ color: "var(--text-secondary)" }}>
        For your own systems to push tickets into Prioris. A key is scoped to this
        workspace, can only ingest, and is shown once.
      </p>

      <form className="card p-4 mb-4" onSubmit={create} data-testid="issue-key-form">
        <div className="flex gap-2">
          <input className="input" required placeholder="What will use this key? e.g. Helpdesk bridge"
                 data-testid="key-name" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn whitespace-nowrap" type="submit" disabled={busy}
                  data-testid="issue-key">
            <KeyRound size={13} /> Issue key
          </button>
        </div>
      </form>

      {error && <div className="badge b-rev mb-3 inline-block" data-testid="keys-error">{error}</div>}

      {issued?.api_key && (
        <div className="card p-4 mb-4" data-testid="issued-key">
          <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
            Copy this now
          </div>
          <button className="btn btn-ghost font-mono text-[12px] break-all text-left"
                  data-testid="copy-key"
                  onClick={() => { navigator.clipboard?.writeText(issued.api_key);
                                   setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
            {copied ? <Check size={12} /> : <Copy size={12} />} {issued.api_key}
          </button>
          <div className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
            Not stored anywhere in a form we can show again. If it is lost, revoke it
            and issue another.
          </div>
        </div>
      )}

      <div className="card overflow-x-auto mb-5" data-testid="keys-table">
        <table className="w-full min-w-[640px] text-[13px]">
          <thead>
            <tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Name</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Key id</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Last used</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Status</th>
              <th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {accounts === null && (
              <tr><td colSpan={5} className="py-8 text-center" style={{ color: "var(--text-muted)" }}>Loading…</td></tr>
            )}
            {accounts?.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center" style={{ color: "var(--text-muted)" }}>
                No keys yet.
              </td></tr>
            )}
            {accounts?.map((a) => (
              <tr key={a.id} style={{ borderBottom: "1px solid var(--border)" }}
                  data-testid={`key-${a.key_id}`}>
                <td className="py-2.5 px-3">{a.name}</td>
                {/* The public half only. It identifies a key in a log without
                    being usable, which is what makes it safe to show. */}
                <td className="py-2.5 px-3 font-mono text-[11.5px]">{a.key_id}</td>
                <td className="py-2.5 px-3">{fmtTimeAgo(a.last_used_at) || "never"}</td>
                <td className="py-2.5 px-3">
                  <span className={`badge ${a.active ? "b-healthy" : "b-muted"}`}>
                    {a.active ? "active" : "revoked"}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right">
                  {a.active && (
                    <button className="btn btn-ghost" style={{ padding: "3px 9px", fontSize: 11 }}
                            data-testid={`revoke-key-${a.key_id}`} onClick={() => revoke(a)}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card p-5" data-testid="push-guide">
        <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
          Sending a ticket
        </div>
        <pre className="font-mono text-[11.5px] overflow-x-auto p-3 rounded-md"
             style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
{`POST /api/v1/ingest
X-Api-Key: prio_...

{
  "source_type": "internal_ticketdb",
  "source_event_id": "TCK-9001",      // stable per ticket — this is what
                                       // makes re-sending safe
  "subject": "Shipment tracking broken",
  "content": "Customers cannot see tracking after the last release.",
  "severity": "high",                  // low | medium | high | critical
  "identity": { "email": "buyer@globex.com" },
  "sla_hours": 8                       // optional
}`}
        </pre>
        <p className="text-[11.5px] mt-2" style={{ color: "var(--text-muted)" }}>
          Re-sending the same <span className="font-mono">source_event_id</span> is
          safe: it is recognised as the ticket you already sent, and does not count
          against your plan a second time.
        </p>
      </div>
    </div>
  );
}
